import React, { useState, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { auth, firestore } from '../firebase-config';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import '../styles/LoginModal.css';

const LoginModal = ({ isOpen, onClose }) => {
  const { loggedInUser, loginUser, logoutUser } = useContext(AuthContext);
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [message, setMessage] = useState('');

  if (!isOpen) return null;

  // Handle sign-up logic using Firebase Authentication
  const handleSignUp = async () => {
    const usernameRegex = /^[a-zA-Z0-9_]+$/;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const offensiveWords = ['shit', 'fuck', 'dick', 'nigger', 'cunt', 'bitch'];

    if (offensiveWords.some(word => username.toLowerCase().includes(word.toLowerCase()))) {
      setMessage('Username contains offensive language. Please choose a different one.');
      return;
    }

    if (!usernameRegex.test(username)) {
      setMessage('Username can only contain letters, numbers, and underscores.');
      return;
    }

    if (!emailRegex.test(email)) {
      setMessage('Please enter a valid email address.');
      return;
    }

    try {
      // Check if username already exists
      const usersRef = collection(firestore, 'users');
      const q = query(usersRef, where('username', '==', username));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        setMessage('Username is already taken. Please choose another.');
        return;
      }

      // Create user in Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Store additional user details in Firestore
      const userRef = doc(firestore, 'users', user.uid);
      await setDoc(userRef, {
        uid: user.uid,
        username,
        email,
        role: 'User',
        buttonScore: 0,
      });

      setMessage('Sign-up successful! You can now log in.');
      setIsLogin(true);
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  };

  // Handle login logic using Firebase Authentication
  const handleLogin = async () => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Fetch user details from Firestore
      const userRef = doc(firestore, 'users', user.uid);
      const userSnap = await getDocs(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        loginUser({ username: userData.username, email: userData.email, role: userData.role });
        setMessage(`Welcome back, ${userData.username}!`);
      } else {
        setMessage('User data not found. Please contact support.');
      }
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      logoutUser();
      setMessage('Logged out successfully.');
      onClose(); // Close modal after logout
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="close-button" onClick={onClose}>×</button>
        {loggedInUser ? (
          <div>
            <h2>Account Details</h2>
            <p><strong>Username:</strong> {loggedInUser.username}</p>
            <p><strong>Email:</strong> {loggedInUser.email}</p>
            <p><strong>Role:</strong> {loggedInUser.role}</p>
            <button onClick={handleLogout} className="logout-button">Log Out</button>
          </div>
        ) : (
          <div>
            <h2>{isLogin ? 'Login' : 'Sign Up'}</h2>
            {!isLogin && (
              <div>
                <label>Username:</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
            )}
            <div>
              <label>Email:</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label>Password:</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {isLogin ? (
              <button onClick={handleLogin} className="action-button">Log In</button>
            ) : (
              <button onClick={handleSignUp} className="action-button">Sign Up</button>
            )}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="toggle-link"
            >
              {isLogin ? "Don't have an account? Sign Up" : 'Already have an account? Log In'}
            </button>
            {message && <p className="message">{message}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginModal;